// Small helper wrapping the Web Share API with a mailto: fallback.
// Mobile-like devices (touchscreen) get the OS share sheet; desktop
// devices skip Web Share and go straight to a `mailto:` URL that
// hands compose off to whatever default mail app the user has set.
// The clipboard is intentionally never touched — see `sharePrayer`.
// Returns one of: 'shared' | 'cancelled' | 'error'.

import api from './api';

// Build a mailto: URL that hands the compose off to the user's default
// mail app (Outlook, Apple Mail, Thunderbird, or Gmail if the user has
// registered it as their mailto handler). Standard mailto params —
// nothing provider-specific — so every desktop mail client gets both
// the subject and the multi-line body intact. `encodeURIComponent`
// preserves line breaks (%0A), spaces, and query-string characters
// inside the URL body.
const buildMailtoUrl = (subject, invitation, url) => {
  const body = `${invitation}\n\n${url}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// Provider-neutral: hand the compose off to the OS's default mail
// handler via a `mailto:` URL. That respects whatever the user has
// configured (Outlook, Apple Mail, Thunderbird, Gmail-as-mailto-handler,
// etc.) instead of forcing them into Gmail's web compose. The mailto
// protocol handoff never navigates the current tab, so the app stays
// on-screen behind the mail composer. Returns true if we successfully
// dispatched some kind of composer.
const openEmailCompose = (subject, invitation, url) => {
  if (typeof window === 'undefined') return false;
  const mailtoUrl = buildMailtoUrl(subject, invitation, url);
  try {
    // Use an anchor click over `location.href = …` — the anchor
    // approach fails silently when no mail handler is registered,
    // whereas assigning `location.href` can navigate the tab to a
    // "protocol not associated" browser error in that edge case.
    const a = document.createElement('a');
    a.href = mailtoUrl;
    a.rel = 'noopener';
    // Same tab is fine — mailto: is a protocol handoff, the browser
    // won't actually navigate the page away.
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch (e) {
    console.warn('email compose open failed', e);
    return false;
  }
};

const buildPrayerShare = (prayer, sharer = null, personalMessage = '') => {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  // Attach `?ref=share` so the destination WatchPrayer page can distinguish
  // arrivals via a shared link from in-app navigation. The frontend strips
  // the marker after recording, so the visible URL stays clean.
  //
  // When a sharer identity is provided, also embed `&shared_by=<user_id>`
  // so the landing page can forward it to `/shared-open` and the sharer
  // gets credited on the recipient's ledger row + activity log.
  const params = new URLSearchParams({ ref: 'share' });
  const sharerId = (sharer && (sharer.user_id || sharer.id)) || null;
  if (sharerId) params.set('shared_by', sharerId);
  // Share links route through the backend preview endpoint (/api/p/:id)
  // so forums / social crawlers get per-prayer Open Graph tags (title,
  // excerpt, image). Human visitors are instantly redirected to
  // /prayer/:id with ref + shared_by intact.
  const url = `${origin}/api/p/${prayer.id}?${params.toString()}`;
  // Payload shape:
  //   • `subject`    — email Subject line ("Pray with me...")
  //   • `invitation` — the human sentence that opens the body
  //   • `url`        — the deep link into WatchPrayer
  //   • `title/text` — Web Share API fields for the mobile OS sheet.
  //     `text` includes the invitation AND the URL so receivers that
  //     drop `url` (Gmail-on-Android is a known offender) still get
  //     both pieces. Desktops without Web Share go through the
  //     dedicated mailto fallback in `sharePrayer` instead.
  //   • `personalMessage` — optional per-share note from the sender
  //     (e.g. "Tom is very sick — let's pray for him"). When present,
  //     it's prepended to `text` and to the email body so receivers
  //     see it FIRST — the human context lands before the boilerplate
  //     invitation + link. Facebook / LinkedIn strip `text`, so the
  //     dialog surfaces a hint to the sharer about that.
  const trimmedNote = (personalMessage || '').trim();
  const subject = 'Pray with me...';
  const baseInvitation = 'This is a link to join me in a prayer on Pray Now Together';
  const invitation = trimmedNote
    ? `${trimmedNote}\n\n${baseInvitation}`
    : baseInvitation;
  return {
    url,
    subject,
    invitation,
    title: subject,
    text: `${invitation}:\n\n${url}`,
    // Exposed as a distinct field so Web Share consumers on mobile
    // can still surface the personal note separately from the URL.
    personalMessage: trimmedNote,
  };
};

// Copy the composed invitation + URL to the clipboard. Only called on
// direct user action (a click on the "Copy invitation" button in the
// share dialog), so the permission prompt — if any — is expected and
// non-intrusive.
const copyInvitationToClipboard = async (payload) => {
  const body = `${payload.invitation}\n\n${payload.url}`;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(body);
    } else {
      const ta = document.createElement('textarea');
      ta.value = body;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    return true;
  } catch (e) {
    console.warn('copy invitation to clipboard failed', e);
    return false;
  }
};

// `mode` selects the delivery channel:
//   'auto'  (default) — native OS share sheet if available, else mailto:.
//   'email'           — always mailto:, bypassing the OS share sheet.
//                       Guarantees the note survives (subject + body land
//                       intact in the user's default mail app).
//   'copy'            — copy the invitation + URL to the clipboard.
//                       User-initiated, so the permission prompt (if any)
//                       is expected.
export const sharePrayer = async (prayer, sharer = null, personalMessage = '', mode = 'auto') => {
  if (!prayer || !prayer.id) return 'error';
  const payload = buildPrayerShare(prayer, sharer, personalMessage);
  // Fire-and-forget share-count bump. Runs on any successful share
  // completion (native sheet, mailto, or clipboard). Failures are
  // silent — the count is nice-to-have, not correctness-critical.
  const bumpShareCount = () => {
    api.post(`/prayers/${prayer.id}/share`).catch((e) => {
      console.warn('share count bump failed', e);
    });
  };

  if (mode === 'copy') {
    const ok = await copyInvitationToClipboard(payload);
    if (!ok) return 'error';
    bumpShareCount();
    return 'copied';
  }

  if (mode === 'email') {
    const opened = openEmailCompose(payload.subject, payload.invitation, payload.url);
    if (!opened) return 'error';
    bumpShareCount();
    return 'shared';
  }

  // mode === 'auto': try the native OS share sheet first — gives the
  // user the standard app chooser (Mail / Messages / WhatsApp / Slack
  // / etc.) on both mobile and desktop. If Web Share is unavailable
  // or throws, we fall through to the mailto: composer.
  //
  // Caveat: some desktop share integrations forward only `url` and
  // silently drop `text`, so the personal note may not survive on
  // every target. The "More ways to share" section in the dialog
  // exposes `email` and `copy` modes for guaranteed note delivery.
  if (typeof navigator !== 'undefined'
      && typeof navigator.share === 'function') {
    try {
      // Web Share ignores non-standard keys, so passing subject/invitation
      // alongside title/text/url is harmless.
      await navigator.share(payload);
      bumpShareCount();
      return 'shared';
    } catch (e) {
      // AbortError = user closed the sheet — treat as cancel, not an error.
      if (e && (e.name === 'AbortError' || /abort|cancel/i.test(e.message || ''))) {
        return 'cancelled';
      }
      // Fall through to the mailto: composer on any other failure.
    }
  }
  const opened = openEmailCompose(payload.subject, payload.invitation, payload.url);
  if (opened) {
    bumpShareCount();
    return 'shared';
  }
  return 'error';
};

export const prayerShareUrl = (prayer, sharer = null) => buildPrayerShare(prayer, sharer).url;

// ---- Circle sharing ----
// A "circle" is identified by its owner's user_id — we route the
// recipient straight to `/circle/:userId`. Same OS-share ⟶ mailto:
// pipeline as `sharePrayer` so buttons on the Joined Circles page and
// the Circle Page header behave identically for the sharer.
const buildCircleShare = (author, personalMessage = '') => {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  const userId = author?.user_id || author?.author_user_id || author?.id;
  // Routes through the backend preview endpoint (/api/c/:userId) so
  // forums / social crawlers get a per-circle card (owner name, bio,
  // profile picture). Humans are instantly redirected to /circle/:userId.
  const url = `${origin}/api/c/${userId}`;
  const handle = author?.handle ? `@${author.handle}` : (author?.name || 'a friend');
  const subject = 'Join this Circle on Pray Now Together';
  const baseInvitation = `${handle}'s prayer circle on Pray Now Together — join me in praying with them`;
  // Same convention as `buildPrayerShare` — the personal note lands
  // FIRST in the composed body so recipients see the human context
  // before the boilerplate invitation and link.
  const trimmedNote = (personalMessage || '').trim();
  const invitation = trimmedNote
    ? `${trimmedNote}\n\n${baseInvitation}`
    : baseInvitation;
  return {
    url,
    subject,
    invitation,
    title: subject,
    text: `${invitation}:\n\n${url}`,
    personalMessage: trimmedNote,
  };
};

export const circleShareUrl = (author) => buildCircleShare(author).url;

export const shareCircle = async (author, personalMessage = '', mode = 'auto') => {
  const payload = buildCircleShare(author, personalMessage);
  if (!payload.url || payload.url.endsWith('/undefined')) return 'error';

  if (mode === 'copy') {
    const ok = await copyInvitationToClipboard(payload);
    return ok ? 'copied' : 'error';
  }

  if (mode === 'email') {
    const opened = openEmailCompose(payload.subject, payload.invitation, payload.url);
    return opened ? 'shared' : 'error';
  }

  // mode === 'auto': try native share first, then mailto — mirrors
  // `sharePrayer`'s auto-mode logic.
  if (typeof navigator !== 'undefined'
      && typeof navigator.share === 'function') {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (e) {
      if (e && (e.name === 'AbortError' || /abort|cancel/i.test(e.message || ''))) {
        return 'cancelled';
      }
      // fall through to the mailto: composer
    }
  }
  const opened = openEmailCompose(payload.subject, payload.invitation, payload.url);
  return opened ? 'shared' : 'error';
};
